import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, Spin } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  CompassOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useScreenWidth from '../../../../hooks/useScreenSize';
import { RootState } from '../../../../store/store';
import { Z_MOBILE_SHEET } from '../../../../utils/mobileLayers';
import {
  focusSheetInput,
  primeMobileKeyboard,
} from '../../../../utils/mobileKeyboardFocus';
import './autoCompleteSheet.less';

/**
 * Phones (width only): the product-search autocompletes (destination,
 * airports, events, packages) don't type inside the small field card.
 * The card shows the current value; tapping it opens a search sheet that
 * hosts the SAME antd AutoComplete — its own search/selection logic is
 * untouched — with the results rendered as an in-sheet list.
 *
 *   field card:  Destination                 sheet: ╭────── ▬ ──────╮
 *                Paris (France)      ›              │ Destination  ✕ │
 *                                                   │ [🔍 search…  ] │
 *                                                   │ result rows    │
 *                                                   ╰────────────────╯
 */
export const useAutoCompleteSheet = () => {
  const { isMobileWidth } = useScreenWidth();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const getContainer = useCallback(
    () => containerRef.current || document.body,
    []
  );

  return {
    isPhone: isMobileWidth,
    isOpen,
    open,
    close,
    containerRef,
    /** AutoComplete props for the in-sheet list (phones only) */
    sheetProps: isMobileWidth
      ? {
          getPopupContainer: getContainer,
          virtual: false,
          dropdownMatchSelectWidth: false as const,
        }
      : {},
  };
};

// The sheet is portalled, but React still bubbles its events through the
// field's parents (search forms with their own click/focus handlers).
const isolate = {
  onClick: (e: React.SyntheticEvent) => e.stopPropagation(),
  onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onTouchStart: (e: React.SyntheticEvent) => e.stopPropagation(),
  onKeyDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onFocus: (e: React.SyntheticEvent) => e.stopPropagation(),
};

/**
 * The options' own elements (AirportElement / CityElement / HotelElement /
 * EventElement) carry the data — read title · code · subtitle from them so
 * the field and the sheet can show a selection as "Paris  PAR  France"
 * instead of the raw option row.
 */
export const describeOption = (
  label: React.ReactNode
): { title?: React.ReactNode; code?: string; subtitle?: string } | null => {
  if (label === null || label === undefined || label === false) return null;
  if (typeof label === 'string' || typeof label === 'number') {
    return { title: String(label) };
  }
  if (!React.isValidElement(label)) return null;
  const p: any = label.props;
  const nameOf = (v: any) => (typeof v === 'string' ? v : v?.name);
  if (p?.data && (p.data.iata_code || p.data.name)) {
    return {
      title: p.data.name ?? p.data.city_name ?? p.data.iata_code,
      code: p.data.iata_code,
      subtitle: nameOf(p.data.country),
    };
  }
  if (p?.name) {
    return { title: p.name, subtitle: nameOf(p.country) };
  }
  return { title: label };
};

const SelectionText = ({
  label,
  className,
}: {
  label: React.ReactNode;
  className: string;
}) => {
  const d = describeOption(label);
  if (!d) return null;
  return (
    <>
      <span className={`${className}__title`}>{d.title}</span>
      {!!d.code && <span className={`${className}__code`}>{d.code}</span>}
      {!!d.subtitle && <span className={`${className}__sub`}>{d.subtitle}</span>}
    </>
  );
};

export const AutoCompleteTrigger = ({
  value,
  placeholder,
  onOpen,
}: {
  value?: React.ReactNode;
  placeholder?: React.ReactNode;
  onOpen: () => void;
}) => (
  <button
    type="button"
    className="ac-trigger"
    aria-haspopup="dialog"
    onClick={(e) => {
      e.stopPropagation();
      // inside the tap, so iOS raises the keyboard for the sheet's input
      primeMobileKeyboard();
      onOpen();
    }}
  >
    {value ? (
      <SelectionText label={value} className="ac-trigger" />
    ) : (
      <span className="ac-trigger__placeholder">{placeholder}</span>
    )}
  </button>
);

export const AutoCompleteSheet = ({
  open,
  onClose,
  title,
  isFetching,
  containerRef,
  current,
  query,
  resultCount,
  hint,
  contextLabel,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  isFetching?: boolean;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  /** the current selection — shown (✓) until the user starts typing */
  current?: React.ReactNode;
  /** what's typed and how many options it produced — drives the empty states */
  query?: string;
  resultCount?: number;
  /** field-specific guidance under "Start typing to search" */
  hint?: React.ReactNode;
  /** why this list — e.g. the selected package type ("Ski") */
  contextLabel?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { general, searchDatePicker: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const q = (query ?? '').trim();
  // nothing typed (and nothing to list yet): what you can search for
  const showStart = !q && !resultCount && !isFetching;
  // the APIs search from 2 characters; below that it's still "typing"
  const showNoResults = q.length >= 2 && !isFetching && !resultCount;

  // Native search keyboard: a "Search" key, no autocorrect on place names.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const input = containerRef.current?.querySelector('input');
      if (!input) return;
      input.setAttribute('enterkeyhint', 'search');
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, containerRef]);

  // Opening the sheet puts the cursor in its search field right away.
  useEffect(() => {
    if (!open) return;
    return focusSheetInput(() => containerRef.current);
  }, [open, containerRef]);

  // Scrolling the results hides the keyboard, like native search screens.
  const onBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const active = document.activeElement as HTMLElement | null;
    if (
      e.currentTarget.scrollTop > 8 &&
      active?.tagName === 'INPUT' &&
      e.currentTarget.contains(active)
    ) {
      active.blur();
    }
  };
  return (
  <Drawer
    open={open}
    onClose={onClose}
    placement="bottom"
    height="auto"
    closable={false}
    push={false}
    destroyOnClose
    zIndex={Z_MOBILE_SHEET}
    className="ac-sheet"
  >
    <div className="ac-sheet__inner" {...isolate}>
      <span className="ac-sheet__grabber" aria-hidden="true" />
      <div className="ac-sheet__head">
        <span className="ac-sheet__title-wrap">
          <span className="ac-sheet__title">{title}</span>
          {!!contextLabel && (
            <span className="ac-sheet__context">
              <TagOutlined />
              {contextLabel}
            </span>
          )}
        </span>
        <button
          type="button"
          className="ac-sheet__close"
          onClick={onClose}
          aria-label={general?.close}
        >
          <CloseOutlined />
        </button>
      </div>
      {/* the AutoComplete's results list mounts in here (getPopupContainer) */}
      <div className="ac-sheet__body" ref={containerRef} onScroll={onBodyScroll}>
        <div className="ac-sheet__field">
          <SearchOutlined className="ac-sheet__search-icon" />
          {children}
          {isFetching && <Spin size="small" className="ac-sheet__spin" />}
        </div>
        {!!current && (
          <button type="button" className="ac-sheet__current" onClick={onClose}>
            <span className="ac-sheet__current-text">
              <SelectionText label={current} className="ac-sheet__current" />
            </span>
            <CheckOutlined className="ac-sheet__current-check" />
          </button>
        )}
        {showStart && (
          <div className="ac-sheet__empty">
            <span className="ac-sheet__empty-icon">
              <SearchOutlined />
            </span>
            <span className="ac-sheet__empty-title">{t?.acStartTyping}</span>
            {!!hint && <span className="ac-sheet__empty-hint">{hint}</span>}
          </div>
        )}
        {showNoResults && (
          <div className="ac-sheet__empty is-no-results">
            <span className="ac-sheet__empty-icon">
              <CompassOutlined />
            </span>
            <span className="ac-sheet__empty-title">
              {/* the query in its own bidi run: a Latin query inside Hebrew
                  text would otherwise reorder its quotation marks */}
              {(t?.acNoResultsTitle ?? '{query}')
                .split('{query}')
                .map((part: string, i: number, arr: string[]) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && <bdi>{q}</bdi>}
                  </React.Fragment>
                ))}
            </span>
            <span className="ac-sheet__empty-hint">{t?.acNoResultsHint}</span>
          </div>
        )}
      </div>
    </div>
  </Drawer>
  );
};
